import { compareByEpisodeAppearance, sortStates } from '../../../utils/sortStates';
import {
  BaseMetadataSource,
  normalizeAssetName,
  ResolvedBaseMetadata,
  resolveBaseMetadata,
} from './baseMetadata';

export { sortStates };

export type AssetSortMode = 'episodeCount' | 'appearance';

export function episodeCount(states: { episodes?: number[] }[]) {
  return new Set(states.flatMap(s => s.episodes || [])).size;
}

function mergedEpisodes(states: { episodes?: number[] }[]): number[] {
  return Array.from(new Set(states.flatMap(s => s.episodes || [])));
}

export interface AnalysisAssetGroup<T> {
  name: string;
  states: T[];
  metadataSources: T[];
  basicState?: T;
  baseMetadata: ResolvedBaseMetadata;
}

export function groupAnalysisAssets<T extends BaseMetadataSource & { episodes?: number[] }>(
  items: T[],
  sortMode: AssetSortMode = 'episodeCount',
): AnalysisAssetGroup<T>[] {
  const groups = new Map<string, T[]>();
  const metadataBuckets = new Map<string, T[]>();
  for (const item of items) {
    const normalizedName = normalizeAssetName(item.name);
    const metadataSources = metadataBuckets.get(normalizedName) || [];
    metadataBuckets.set(normalizedName, [...metadataSources, item]);

    if (item.state === 'basic') {
      continue;
    }
    const list = groups.get(item.name) || [];
    groups.set(item.name, [...list, item]);
  }

  const resolvedBuckets = new Map<string, ResolvedBaseMetadata>();
  const grouped = Array.from(groups.entries())
    .map(([name, states]) => {
      const normalizedName = normalizeAssetName(name);
      const metadataSources = metadataBuckets.get(normalizedName) || [];
      let baseMetadata = resolvedBuckets.get(normalizedName);
      if (!baseMetadata) {
        baseMetadata = resolveBaseMetadata(metadataSources);
        resolvedBuckets.set(normalizedName, baseMetadata);
      }
      return {
        name,
        states: sortStates(states),
        metadataSources,
        basicState: metadataSources.find(item => item.state === 'basic'),
        baseMetadata,
      };
    });

  if (sortMode === 'appearance') {
    // mergedEpisodes 已去重，满足 compareByEpisodeAppearance 的"调用方负责去重"契约
    return grouped
      .map(group => ({ group, merged: mergedEpisodes(group.states) }))
      .sort((a, b) => compareByEpisodeAppearance(a.merged, b.merged))
      .map(entry => entry.group);
  }
  return grouped.sort((a, b) => episodeCount(b.states) - episodeCount(a.states));
}

export function filterGroupsByEpisodes<
  T extends { episodes?: number[] },
  G extends { name: string; states: T[]; basicState?: T },
>(
  groups: G[],
  episodeIndexes: number[],
): G[] {
  const wanted = new Set(episodeIndexes);
  return groups
    .map(group => ({
      ...group,
      states: group.states.filter(state => state.episodes?.some(episode => wanted.has(episode))),
    }) as G)
    .filter(group => group.states.length > 0);
}

export function collectAssetEpisodes(...assetLists: Array<Array<{ episodes?: number[] }>>) {
  const set = new Set<number>();
  for (const assets of assetLists) {
    for (const asset of assets) {
      (asset.episodes || []).forEach(episode => set.add(episode));
    }
  }
  return Array.from(set).sort((a, b) => a - b);
}
